<template>
    <div ref="graphRoot" class="linear-graph">
        <svg
            v-if="loaded"
            ref="graphElement"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            :class="{ loaded }"
            :style="{ backgroundColor: props.backgroundColor }"
            :viewBox="`0 0 ${sizes.width} ${sizes.height}`"
        >
            <defs>
                <linearGradient
                    id="fill-gradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                >
                    <stop offset="50%" :stop-color="props.gradientTopColor" />
                    <stop offset="100%" :stop-color="props.gradientBottomColor" />
                </linearGradient>
            </defs>

            <path
                :d="areaCurvePath"
                fill="url(#fill-gradient)"
                class="graph-area"
            />

            <path
                :d="lineCurvePath"
                :stroke-width="props.lineSize"
                :stroke="props.lineColor"
                fill="none"
                class="graph-line"
            />

            <circle
                v-for="(point, index) in actualPoints"
                :key="index"
                v-tooltip.instant="data[index].label || data[index].toString()"
                :fill="props.dotColor"
                :cx="point.x"
                :cy="point.y"
                :r="props.dotSize"
            />
        </svg>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import { getCssColor } from '@keyvue/one-ui';

    interface GraphData {
        value: number,
        label?: string,
    }

    interface Props {
        data: GraphData[];
        backgroundColor?: string;
        dotSize?: number;
        dotColor?: string;
        lineSize?: number;
        lineColor?: string;
        gradientTopColor?: string;
        gradientBottomColor?: string;
        paddingTop?: number;
        paddingBottom?: number;
        paddingLeft?: number;
        paddingRight?: number;
    }

    const props = withDefaults(defineProps<Props>(), {
        backgroundColor: 'transparent',
        dotSize: 4,
        dotColor: getCssColor('default', 'lemon-yellow-500'),
        lineSize: 3,
        lineColor: getCssColor('default', 'turquoise-500'),
        gradientTopColor: '#ebf8f5',
        gradientBottomColor: getCssColor('default', 'white'),
        paddingTop: 20,
        paddingBottom: 30,
        paddingLeft: 0,
        paddingRight: 0,
    });

    const resizeObserver = ref<ResizeObserver | null>();
    const graphRoot = ref<HTMLDivElement | null>(null);
    const graphElement = ref<SVGElement | null>(null);
    const loaded = ref(false);

    const sizes = ref({
        width: 0,
        height: 0,
    });

    const scaledData = computed(() => {
        if (!props.data.length) return [];

        const values = props.data.map((d) => d.value);
        const max = Math.max(...values);
        const min = Math.min(...values);
        const range = max - min || 1;

        return props.data.map((d) => ({
            ...d,
            value: (d.value - min) / range,
        }));
    });

    const actualPoints = computed(() => {
        if (!scaledData.value.length) return [];

        const effectiveWidth = sizes.value.width - props.paddingLeft - props.paddingRight;
        const step = effectiveWidth / (scaledData.value.length - 1 || 1);

        return scaledData.value.map((d, i) => {
            const x = props.paddingLeft + i * step;
            const y = (1 - d.value)
                * (sizes.value.height - props.paddingTop - props.paddingBottom)
                + props.paddingTop;

            return { x, y };
        });
    });

    const extendedPoints = computed(() => {
        const points = actualPoints.value;
        if (points.length <= 1) {
            return points;
        }

        const beforeFirst = {
            x: 0,
            y: points[0].y,
        };

        const afterLast = {
            x: sizes.value.width,
            y: points[points.length - 1].y,
        };

        return [beforeFirst, ...points, afterLast];
    });

    const lineCurvePath = computed(() => {
        return getSmoothPath(extendedPoints.value);
    });

    const areaCurvePath = computed(() => {
        if (!extendedPoints.value.length) return '';

        const curve = getSmoothPath(extendedPoints.value);
        const last = extendedPoints.value[extendedPoints.value.length - 1];
        const first = extendedPoints.value[0];

        return `${curve} L ${last.x},${sizes.value.height} L ${first.x},${sizes.value.height} Z`;
    });

    function getSmoothPath(coords: { x: number; y: number }[]) {
        if (coords.length < 2) return '';

        const d = [`M ${coords[0].x},${coords[0].y}`];
        for (let i = 0; i < coords.length - 1; i += 1) {
            const p0 = coords[i];
            const p1 = coords[i + 1];
            const controlX = (p0.x + p1.x) / 2;
            d.push(`C ${controlX},${p0.y} ${controlX},${p1.y} ${p1.x},${p1.y}`);
        }

        return d.join(' ');
    }

    function recalculateSizes() {
        if (!graphRoot.value) {
            return;
        }

        sizes.value.width = graphRoot.value.clientWidth;
        sizes.value.height = graphRoot.value.clientHeight;
    }

    onMounted(() => {
        recalculateSizes();
        loaded.value = true;

        if (graphRoot.value) {
            resizeObserver.value = new ResizeObserver(() => {
                recalculateSizes();
            });

            resizeObserver.value.observe(graphRoot.value);
        }
    });

    onUnmounted(() => {
        resizeObserver.value?.disconnect();
    });

    // fix: tooltips rendering incorrect label due to vue element recycling
    watch(scaledData, () => {
        loaded.value = false;
        setTimeout(() => { loaded.value = true; }, 1);
    });
</script>

<style scoped lang="scss">
    .graph-line,
    .graph-area,
    circle {
        opacity: 0;
        transform: translateY(10px);
        transition: opacity 0.6s ease, transform 0.6s ease;
        transition-delay: .2s;
    }

    .linear-graph {
        width: 100%;
        height: 100%;
        position: relative;

        & svg.loaded .graph-line,
        & svg.loaded .graph-area,
        & svg.loaded circle {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>
